<template>
    <!-- Slider Variant (PDP, Desktop Cart) -->
    <div v-if="variant === 'slider' && products.length > 0" ref="containerRef" class="my-8">
        <h2 v-if="title" class="text-lg font-bold mb-4">
            {{ title }}
        </h2>
        <ProductSlider
            :slider-id="sliderId"
            :products="products"
            :product-qty="productQty"
            slider-title="Desktop Recommendations"
            :item-list-id="itemListId"
            :item-list-name="itemListName"
            :is-offcanvas="isOffcanvas"
            :display-nav="displayNav"
            :pagination="false"
            class="recommendations-slider"
            @on-listing-card-click="onSliderCardClick($event.product, $event.event)"
        />
    </div>

    <!-- List Variant (Desktop Cart) -->
    <template v-else-if="variant === 'list'">
        <div
            v-if="products.length > 0"
            ref="listContainerRef"
            class="flex-1 min-h-0"
            :class="{ 'overflow-y-auto': isOffcanvas }">
            <h2 v-if="title" class="text-sm font-semibold mb-3 lg:mb-10">
                {{ title }}
            </h2>
            <div class="grid grid-cols-2 gap-4">
                <ProductListingCard
                    v-for="(item, index) in products"
                    :key="item.data.id"
                    :data="item.data"
                    :index="index"
                    :lazy-image="true"
                    @click="onListingCardClick(item.data, index)"
                />
            </div>
        </div>
    </template>

    <!-- Modal Variant (Mobile Cart) -->
    <Teleport v-else-if="variant === 'modal'" to="body">
        <div
            v-if="isVisible"
            ref="modalRef"
            class="fixed inset-0 z-[100001] bg-base-100 overflow-y-auto"
        >
            <div class="flex flex-col h-full">
                <!-- Header -->
                <div class="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-base-100 z-10">
                    <h2 class="text-lg font-bold">
                        {{ title }}
                    </h2>
                    <button
                        type="button"
                        class="btn btn-ghost btn-circle"
                        :aria-label="$t('drawer.close')"
                        @click="handleClose"
                    >
                        <MiscIcon name="x" />
                    </button>
                </div>

                <!-- Content -->
                <div v-if="loading" class="flex-1 p-4">
                    <div class="grid grid-cols-2 gap-4">
                        <div v-for="i in 8" :key="i" class="skeleton h-64" />
                    </div>
                </div>

                <div v-else-if="products.length > 0" class="flex-1 p-4">
                    <div class="grid grid-cols-2 gap-4">
                        <ProductListingCard
                            v-for="(item, index) in products"
                            :key="item.data.id"
                            :data="item.data"
                            :index="index"
                            :lazy-image="true"
                            @click="onListingCardClick(item.data, index)"
                        />
                    </div>
                </div>

                <div v-else class="flex-1 flex items-center justify-center p-4">
                    <p class="text-base-content-subtle">
                        {{ $t('8select.recommendations.empty') }}
                    </p>
                </div>
            </div>
        </div>
    </Teleport>

    <!-- Loading State for non-modal variants -->
    <div v-else-if="loading" class="flex-1 min-h-0" :class="{ 'my-8': !isOffcanvas, 'overflow-y-auto': isOffcanvas }">
        <div v-if="title" class="skeleton h-6 w-48 mb-4" />
        <div v-if="variant === 'slider'" class="skeleton w-full aspect-[5/4] sm:aspect-[377/200]" />
        <div v-else class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div v-for="i in 8" :key="i" class="skeleton h-64" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { use8selectTracking } from '@/composables/use8selectTracking'
import { useIntersectionObserver } from '@vueuse/core'
import type { ListingItemProduct } from '@/types/ListingItems'
import type { Schemas } from '#shopware'
import { productIncludes } from '~/utils/includes/product'

interface EightSelectProductSet {
    products: {
        edges: Array<{
            node: {
                id: string
                variants: {
                    edges: Array<{
                        node: {
                            id: string
                        }
                    }>
                }
            }
        }>
    }
}

interface Props {
    // Source configuration
    productIds: string[]

    // Display configuration
    variant?: 'slider' | 'list' | 'modal'
    title: string
    sliderId?: string
    lazyImage?: boolean
    productQty?: { value: string, source: string }

    // Tracking configuration
    itemListId?: string
    itemListName?: string

    // Behavior configuration
    isVisible?: boolean              // Control visibility for modal variant
    isOffcanvas?: boolean            // Control overflow behavior for list variant
    displayNav?: boolean             // Control navigation display for slider variant
}

const props = withDefaults(defineProps<Props>(), {
    variant: 'slider',
    sliderId: '8select-recommendations',
    itemListId: '8select_recommendations',
    itemListName: '8select Recommendations',
    lazyImage: true,
    isVisible: false,
    isOffcanvas: false,
    displayNav: true,
    productQty: () => { return { value: '6', source: 'value' } }
})

const emit = defineEmits<{
    close: []
    loaded: [count: number]
    error: [error: Error]
}>()

const config = useRuntimeConfig()
const itemListContextStore = useItemListContextStore()
const nuxtApp = useNuxtApp()
const { trackView, trackInteract } = use8selectTracking()
const { apiClient } = useShopwareContext()

const products = ref<ListingItemProduct[]>([])
const loading = ref(false)
const error = ref<Error | null>(null)
const sourceProductId = ref<string>('')
const hasTrackedView = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const modalRef = ref<HTMLElement | null>(null)
const listContainerRef = ref<HTMLElement | null>(null)

/**
 * Round-robin merge algorithm for product sets
 * Takes one product from each set in order, repeating until all products are merged
 * Deduplicates products to avoid adding the same SKU multiple times
 */
const mergeProductSetsRoundRobin = (
    productSets: EightSelectProductSet[]
): string[] => {
    const merged: string[] = []
    const seenSkus = new Set<string>()
    const pointers = productSets.map(() => 0)
    let hasMore = true

    while (hasMore) {
        hasMore = false

        productSets.forEach((set, setIndex) => {
            const products = set.products.edges
            const currentPointer = pointers[setIndex]
            if (currentPointer !== undefined && currentPointer < products.length) {
                const product = products[currentPointer]?.node
                if (product) {
                    // Use the first variant's ID to get the specific color variant 8select
                    // matched, falling back to the product ID if no variant is returned.
                    // Both are in the 8select ID format "6831113_schwarz_01" -> "6831113".
                    const matchedId = product.variants?.edges?.[0]?.node?.id || product.id
                    const productSku = matchedId.split('_')[0] || matchedId

                    // Only add if we haven't seen this SKU before
                    if (!seenSkus.has(productSku)) {
                        merged.push(productSku)
                        seenSkus.add(productSku)
                    }

                    pointers[setIndex] = currentPointer + 1
                    hasMore = true
                }
            }
        })
    }

    return merged
}

/**
 * Fetch product sets from 8select GraphQL API
 */
const fetch8selectProductSets = async (productId: string): Promise<string[]> => {
    const apiId = config.public.eightSelectApiId

    if (!apiId) {
        console.error('[8select] API ID not configured')
        return []
    }

    if (!productId) {
        console.warn('[8select] No product ID provided')
        return []
    }

    const query = `
        query GetMatchingProductClusters($productId: ID!, $clustersCount: Int!, $productsPerCluster: Int!) {
            product(id: $productId) {
                matchingProductClusters(first: $clustersCount) {
                    edges {
                        node {
                            products(first: $productsPerCluster) {
                                edges {
                                    node {
                                        id
                                        variants {
                                            edges {
                                                node {
                                                    id
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `

    try {
        const response = await $fetch<{
            data: {
                product: {
                    matchingProductClusters: {
                        edges: Array<{ node: EightSelectProductSet }>
                    }
                }
            }
        }>(
            'https://api.8select.io/graphql',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-id': apiId
                },
                body: {
                    query,
                    variables: {
                        productId,
                        clustersCount: 4,
                        productsPerCluster: 2
                    }
                }
            }
        )

        if (!response?.data?.product?.matchingProductClusters?.edges) {
            console.warn('[8select] No matching product sets found for product:', productId)
            return []
        }

        const productSets = response.data.product.matchingProductClusters.edges.map(edge => edge.node)

        if (productSets.length === 0) {
            console.warn('[8select] Empty product sets for product:', productId)
            return []
        }

        // Merge products using round-robin algorithm
        const mergedProducts = mergeProductSetsRoundRobin(productSets)

        console.log(`[8select] Found ${productSets.length} product sets, merged ${mergedProducts.length} products`)

        return mergedProducts
    } catch (err) {
        console.error('[8select] Error fetching product sets:', err)
        if (err instanceof Error) {
            console.error('[8select] Error details:', err.message)
        }
        return []
    }
}

/**
 * Main fetch function - handles both single product and cart scenarios
 */
const fetchRecommendations = async () => {
    loading.value = true
    error.value = null

    try {
        let productSkus: string[] = []

        // Determine which product ID to use for 8select
        if (props.productIds && props.productIds.length > 0) {
            // Multiple products (cart use case) - use first product
            const firstProductId = props.productIds[0] as string
            sourceProductId.value = firstProductId
            productSkus = await fetch8selectProductSets(firstProductId)
        } else {
            console.warn('[8select] No product ID(s) provided')
            loading.value = false
            return
        }

        if (productSkus.length === 0) {
            products.value = []
            loading.value = false
            return
        }

        // Fetch full product data from Shopware
        const { data } = await apiClient.invoke("readProduct post /product", {
            body: {
                includes: productIncludes,
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'productNumber',
                        value: productSkus.join('|')
                    }
                ],
                limit: productSkus.length,
                associations: {
                    media: {
                        associations: {
                            media: {}
                        }
                    },
                    cover: { associations: { media: {} } },
                    options: { associations: { group: {} } },
                    properties: { associations: { group: {} } },
                    seoUrls: {}
                }
            },
        })

        // Reorder products to match the original SKU order from round-robin merge
        // Shopware doesn't preserve the order of equalsAny filter values
        const orderedProducts = productSkus
            .map(sku => data.elements?.find(p => p.productNumber === sku))
            .filter((p): p is NonNullable<typeof p> => p !== undefined)

        // Map to HblListingItemProduct format
        const mappedProducts = orderedProducts.map((product, index) => ({
            pos: index,
            type: 'product' as const,
            data: product
        }))

        products.value = mappedProducts

        // Emit loaded event
        emit('loaded', mappedProducts.length)
    } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err))
        error.value = errorObj
        emit('error', errorObj)
        console.error('[8select] Error loading recommendations:', errorObj)
    } finally {
        loading.value = false
    }
}

/**
 * Track the 8select-specific interact event when a recommendation is clicked.
 */
const trackSelectInteract = (product: Schemas['Product'], event: Event | null) => {
    if (event?.target instanceof HTMLElement && event.target.closest('a')) {
        const productSku = product.productNumber
        if (productSku && sourceProductId.value) {
            trackInteract(productSku, sourceProductId.value, 'click')
        }
    }
}

/**
 * Slider variant click handler.
 * ProductSlider already sets the item-list context and fires the
 * `clickProductListingCard` hook (i.e. the GTM `select_item` event), so here we
 * only add the 8select-specific interact tracking. Firing the hook again here
 * caused `select_item` to be sent twice (A4G-3222).
 */
const onSliderCardClick = (product: Schemas['Product'], event: Event | null = null) => {
    trackSelectInteract(product, event)
}

/**
 * List / modal variant click handler.
 * These variants render ProductListingCard directly (no ProductSlider), so this
 * component is the sole owner of the list context and the `select_item` event.
 */
const onListingCardClick = (product: Schemas['Product'], index: number, event: Event | null = null) => {
    itemListContextStore.setItemListContextData({
        item_list_id: props.itemListId,
        item_list_name: props.itemListName,
        index
    })

    trackSelectInteract(product, event)

    // Emit GTM event
    nuxtApp.callHook('clickProductListingCard', {
        id: props.itemListId,
        name: props.itemListName,
        product,
        index
    })
}

/**
 * Handle modal close
 */
const handleClose = () => {
    emit('close')
}

// Mount behavior for slider/grid variants
onMounted(() => {
    if (props.variant !== 'modal') {
        fetchRecommendations()
    }
})

// Watch isVisible for modal variant
watch(
    () => props.isVisible,
    async (newValue) => {
        if (props.variant === 'modal' && newValue && products.value.length === 0) {
            await fetchRecommendations()
        }

        if (props.variant === 'modal' && newValue) {
            const items = products.value.map((listingItem) => listingItem.data)
            useItemListObserver(
                modalRef,
                props.itemListId,
                props.itemListName,
                items
            )
        }
    },
    { immediate: true }
)

// Track view_item_list for the 'list' variant (desktop cart drawer). The 'slider' variant
// gets this from ProductSlider's own useItemListObserver call, and the 'modal' variant sets
// it up above when opened — this branch renders a plain grid with none of that wiring, which
// is why the desktop cart drawer never fired view_item_list for its recommendations (A4G-3332).
watch(products, (newProducts) => {
    if (props.variant !== 'list' || newProducts.length === 0) return

    useItemListObserver(
        listContainerRef,
        props.itemListId,
        props.itemListName,
        newProducts.map(item => item.data)
    )
}, { once: true })

// Watch productIds changes - re-fetch recommendations for the newly selected variant (slider,
// e.g. PDP) or for cart content changes while the modal is open
watch(
    () => props.productIds,
    (newIds, oldIds) => {
        if (JSON.stringify(newIds) === JSON.stringify(oldIds)) {
            return
        }

        if (props.variant === 'slider' || (props.variant === 'modal' && props.isVisible)) {
            fetchRecommendations()
        }
    }
)

// Track view event when recommendations become visible
useIntersectionObserver(
    containerRef,
    (entries) => {
        const entry = entries[0]

        if (!entry?.isIntersecting || hasTrackedView.value || !sourceProductId.value) return

        const viewportHeight = window.innerHeight
        const boundingRect = entry.boundingClientRect

        // Condition 1: 50% of element visible (via intersectionRatio)
        const isHalfElementVisible = entry.intersectionRatio >= 0.5

        // Condition 2: Element fills 50% of viewport
        const visibleHeight = Math.min(boundingRect.bottom, viewportHeight) -
                             Math.max(boundingRect.top, 0)

        const isHalfViewportFilled = visibleHeight >= (viewportHeight / 2)

        if (isHalfElementVisible || isHalfViewportFilled) {
            trackView(sourceProductId.value)
            hasTrackedView.value = true
        }
    },
    { threshold: [0, 0.5, 1] }
)
</script>
